/**
 * The live wiring: the app's single error reporter, and its installation.
 *
 * Split from `errorReporter.ts` on purpose. That module is pure — no DOM, no
 * Firebase — because `web/` has no test runner of its own and its logic is
 * exercised from `functions` under a NODE environment (see
 * `functions/vitest.web.config.ts`). Everything that touches `window` or the
 * Firebase SDK lives here, where it is thin enough to read in one screen.
 */
import { httpsCallable, setCallableFailureListener } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS } from '../../constants/firebase';
import {
  createErrorReporter,
  installGlobalErrorReporting,
  type ClientErrorReport,
  type ErrorReporter,
} from './errorReporter';

/**
 * The transport.
 *
 * `httpsCallable` sends an ID token when one exists and simply omits it when
 * the user is signed out — which is why an error on the login page reports at
 * all. The endpoint tolerates an anonymous caller and rate-limits it by IP.
 */
function send(report: ClientErrorReport): Promise<unknown> {
  const callable = httpsCallable(functions, CLOUD_FUNCTIONS.REPORT_CLIENT_ERROR);
  return callable(report);
}

/**
 * The app's reporter. A module-level singleton, so the dedupe window is shared
 * across every capture point rather than being per-component — a render loop
 * inside a boundary and the unhandledrejection it also triggers should count
 * once, not twice.
 *
 * The route comes from `window.location`, not from a router hook: the
 * ErrorBoundary sits ABOVE `RouterProvider` in `main.tsx` and has no router
 * context to read. With `createBrowserRouter` the URL *is* the router's current
 * location, so the two agree.
 */
export const clientErrorReporter: ErrorReporter = createErrorReporter({
  send,
  pathname: () => (typeof window === 'undefined' ? '/' : window.location.pathname),
});

/** Report a render failure. Called by ErrorBoundary.componentDidCatch. */
export function reportRenderError(): void {
  clientErrorReporter.reportRenderError();
}

/**
 * Attach the two global capture points to the real `window`.
 *
 * Returns an uninstaller, which nothing uses in production — the app never
 * tears down — but which keeps the function honest and testable.
 */
export function installClientErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => undefined;

  // A call refused before it reached our server, after its retries: tell the
  // platform which call it was. The listener receives the name only.
  setCallableFailureListener(({ callableName }) => {
    clientErrorReporter.reportCallableNotReached({ callableName });
  });

  const uninstallGlobal = installGlobalErrorReporting({
    target: window as unknown as {
      addEventListener: (type: string, fn: (e: unknown) => void) => void;
      removeEventListener: (type: string, fn: (e: unknown) => void) => void;
    },
    // The SAME singleton the boundary uses, so one dedupe window covers both.
    reporter: clientErrorReporter,
  });

  return () => {
    setCallableFailureListener(null);
    uninstallGlobal();
  };
}
